<template>
    <div>
        <!-- Add class 'custom-table' to make table adaptive for height -->
        <CDataTable
            class="orders large"
            hover
            :items="list"
            :fields="fields"
        >
            <template #id="data">
                <td>
                    <CLink :to="`/orders/${data.item.id}`">#{{ data.item.id }}</CLink>
                </td>
            </template>
            <template #type="data">
                <td>{{ orderTypeTitle(data.item.type, data.item) }}</td>
            </template>
            <template #created_at="data">
                <td>{{ getCreatedAt(data.item.created_at) }}</td>
            </template>
            <template #updated_at="data">
                <td>{{ getUpdatedAt(data.item.logs) }}</td>
            </template>
            <template #status="data">
                <td class="status-cell">
                    <CBadge :style="orderStatusStyle( data.item.status, data.item.type, data.item.payment_type)">&nbsp;{{ orderStatusTitle(data.item) }}&nbsp;</CBadge>
                </td>
            </template>
            <template #courier="data">
                <td>
                    <div v-if="data.item.courier">
                        <CLink :to="`/couriers/${data.item.courier.id}`">
                            {{ data.item.courier.name }}
                            <span :class="{ dot: true, seen: data.item.courier_approve }"></span>
                        </CLink>
                    </div>
                    <div v-for="assignee in getCourierAssignees(data.item)" :key="assignee.user_id">
                        <CLink :to="`/couriers/${assignee.user_id}`">
                            {{ assignee.user_details.name }}
                        </CLink>
                    </div>
                    <span v-if="!data.item.courier && !getCourierAssignees(data.item).length">Нет</span>
                </td>
            </template>
            <template #concierge="data">
                <td>
                    <div v-if="data.item.concierge">
                        <CLink :to="`/concierges/${data.item.concierge.id}`">
                            {{ data.item.concierge.name }}
                            <span :class="{ dot: true, seen: data.item.concierge_approve }"></span>
                        </CLink>
                    </div>
                    <div v-for="assignee in getConciergeAssignees(data.item)" :key="assignee.user_id">
                        <CLink :to="`/concierges/${assignee.user_id}`">
                            {{ assignee.user_details.name }}
                        </CLink>
                    </div>
                    <span v-if="!data.item.concierge && !getConciergeAssignees(data.item).length">Нет</span>
                </td>
            </template>
            <template #delivery_time="data">
                <td v-html="getDeliveryTime(data.item)"></td>
            </template>
            <template #time_remaining="data">
                <td
                    :class="{
                        'warning-cell': checkTimeRemaining(data.item)
                    }">{{ getTimeRemaining(data.item) }}</td>
            </template>
            <template #companies="data">
                <td>
                    <ul>
                        <li v-for="link in getCompanies(data.item)" :key="link.title">
                            <CLink :to="link.link">{{ link.title }}</CLink><br />
                        </li>
                    </ul>
                </td>
            </template>
            <template #tower="data">
                <td>{{ getTower(data.item) }}</td>
            </template>
            <template #delivery_address="data">
                <td :title="getDeliveryAddress(data.item)">
                    <span
                        :class="{
                            pass__info: true,
                            passed: data.item.pass === PASS_TYPE.PASSED,
                            not__need: data.item.pass === PASS_TYPE.NOT_NEED,
                            not__passed: data.item.pass === PASS_TYPE.NOT_PASSED,
                            without__delivery: data.item.delivery_type === DELIVERY_TYPE.WITHOUT
                        }"
                        :title="getPassTitle(data.item.pass)"></span>
                    {{ cutDeliveryAddress(getDeliveryAddress(data.item)) }}
                </td>
            </template>
            <template #customer="data">
                <td>
                    <CLink :to="`/customers/${data.item.customer.id}`">{{
                        getCustomer(data.item.customer)
                    }}</CLink>
                </td>
            </template>
            <template #comment="data">
                <td :title="data.item.comment">{{ data.item.comment ? cutComment(data.item.comment) : "" }}</td>
            </template>
            <template #customer_comment="data">
                <td :title="data.item.customer_comment">{{ data.item.customer_comment ? cutComment(data.item.customer_comment) : "" }}</td>
            </template>
            <template #rating="data">
                <td>{{ data.item.review ? data.item.review.rating : data.item.rating }}</td>
            </template>
            <template #tips="data">
                <td>{{ data.item.review ? data.item.review.tips : '' }}</td>
            </template>
            <template #price="data">
                <td>{{ data.item.price }}</td>
            </template>
        </CDataTable>
    </div>
</template>

<script>
    import moment from 'moment';
    import countdown from 'countdown';
    require('moment-countdown');

    import { ORDER_STATUS, DELIVERY_TYPE, ORDER_TYPE, PASS_TYPE } from '../../utils/order';
    const {
        orderStatusTitle,
        orderStatusStyle,
        orderTypeTitle,
    } = require('../../utils/order-views');
    const {
        formatDateShort,
        formatTimeShort,
    } = require('../../utils/views');

    export default {

        props: {
            list: {
                type: Array,
                default: () => [],
            },
        },

        data() {
            return {
                isAuthorized: false,
                fieldsAll: [
                    { key: 'id', label: 'ID', _classes: 'font-weight-bold' },
                    { key: 'type', label: 'Тип' },
                    { key: 'created_at', label: 'Создан' },
                    { key: 'updated_at', label: 'Обновлен' },
                    { key: 'status', label: 'Статус' },
                    { key: 'courier', label: 'Курьер' },
                    { key: 'concierge', label: 'Консьерж' },
                    { key: 'delivery_time', label: 'Доставка' },
                    { key: 'time_remaining', label: 'Ост.' },
                    { key: 'companies', label: 'Организации' },
                    { key: 'tower', label: 'Башня', admin_only: true },
                    { key: 'delivery_address', label: 'Адрес', admin_only: true },
                    { key: 'comment', label: 'Комментарий администратора' },
                    { key: 'customer_comment', label: 'Комментарий пользователя' },
                    { key: 'customer', label: 'Клиент', admin_only: true },
                    { key: 'price', label: 'Цена' },
                ],
                PASS_TYPE,
                DELIVERY_TYPE
            };
        },

        computed: {
            fields() {
                const result = [];
                for (const item of this.fieldsAll) {
                    if(this.adminOnly || !item.admin_only) {
                        result.push(item);
                    }
                }
                return result;
            },
            adminOnly() {
                return !JSON.parse(localStorage.getItem('settings'))
                    .is_business_admin;
            },
        },
        
        methods: {
            orderTypeTitle(orderTypeId, item = {}) {
                if (orderTypeId === ORDER_TYPE.ASSISTANT_HELP) {
                    return 'ПМ';
                }
                if (orderTypeId === ORDER_TYPE.ENTITY_DELIVERY && item?.ed?.items) {
                    const { entity } = item.ed.items[0];
                    return entity.entityType.title.charAt(0);
                }
                return orderTypeTitle(orderTypeId).charAt(0);
            },
            getCreatedAt(created_at) {
                const ca = moment(created_at);
                if (ca.isSame(new Date(), 'day')) {
                    return formatTimeShort(ca.toDate());
                } else {
                    return formatDateShort(ca.toDate());
                }
            },
            getUpdatedAt(logs = []) {
                let ISOString = new Date().toISOString();
                
                if (logs?.length) {
                    ISOString = logs[logs.length - 1].created_at;
                }
                countdown.setLabels(
                    'мс|с|м|ч|д|н|мес|г|дек|век|мил',
                    'мс|с|м|ч|д|н|мес|г|дек|век|мил',
                    ' ',
                    ', '
                );
                const updatedAt = countdown(
                    new Date(ISOString),
                    new Date(),
                    countdown.DAYS | countdown.HOURS | countdown.MINUTES
                ).toString();
                return updatedAt ? updatedAt : 'сейчас';
            },
            getCustomer(customer) {
                let customerInfo = '';
                if (customer.phone) {
                    customerInfo += customer.phone;
                }
                if (customer.firstname) {
                    customerInfo += ' ' + customer.firstname;
                }
                if (customer.lastname) {
                    customerInfo += ' ' + customer.lastname;
                }
                return customerInfo;
            },
            orderStatusTitle(order) {
                if (order.ed?.items?.length) {
                    return orderStatusTitle(
                        order.status,
                        order.type,
                        order.payment_type,
                        order.ed?.items[0]?.entity?.statuses,
                        order.delivery_type
                    );
                }
                return orderStatusTitle(
                    order.status,
                    order.type,
                    order.payment_type,
                    null,
                    order.delivery_type
                );
            },
            orderStatusStyle(orderStatusId, orderType, paymentType) {
                return orderStatusStyle(orderStatusId, orderType, paymentType);
            },
            getDeliveryAddress(item) {
                let address = '';
                if (!item.towerObj && item.address) {
                    address = item.address.street + ' ' + item.address.building + ', ';
                }
                if (item.delivery_type === DELIVERY_TYPE.MEETING_POINT) {
                    address = 'точка встречи';
                } else if (item.delivery_type === DELIVERY_TYPE.WITHOUT) {
                    address = 'без доставки';
                } else if (item.address_details) {
                    address +=
                        'вх. ' +
                        item.address_details.entrance +
                        ', эт. ' +
                        item.address_details.floor +
                        ', оф. ' +
                        item.address_details.appartment;
                }
                return address;
            },
            getTower(item) {
                return item.towerObj
                    ? item.towerObj.short_name
                        ? item.towerObj.short_name
                        : item.towerObj.name
                    : '';
            },
            getCompanies(item) {
                const result = {};
                if (item && item.rd && item.rd.items) {
                    for (const orderItem of item.rd.items) {
                        if (orderItem.restaurant && orderItem.restaurant.id) {
                            result[orderItem.restaurant.id] = {
                                link: '/restaurants/' + orderItem.restaurant.id,
                                title:
                                    (orderItem.restaurant.short_title
                                        ? orderItem.restaurant.short_title
                                        : orderItem.restaurant.title) +
                                    (orderItem.restaurant?.towerObj?.short_name
                                    ? ` (${orderItem.restaurant.towerObj?.short_name})`
                                    : ''),
                            };
                        }
                    }
                } else if (item && item.ed && item.ed.items) {
                    for (const orderItem of item.ed.items) {
                        if (orderItem.entity && orderItem.entity.id) {
                            result[orderItem.entity.id] = {
                                link: `entities/${orderItem.entity.entityType.code}/${orderItem.entity.id}`,
                                title:
                                    (orderItem.entity.short_name
                                        ? orderItem.entity.short_name
                                        : orderItem.entity.title) +
                                    (orderItem.entity?.towerObj?.short_name
                                    ? ` (${orderItem.entity.towerObj?.short_name})`
                                    : ''),
                            };
                        }
                    }
                } else if (item && item.fd && item.fd.items) {
                    for (const orderItem of item.fd.items) {
                        if (orderItem.florist && orderItem.florist.id) {
                            result[orderItem.florist.id] = {
                                link: '/florists/' + orderItem.florist.id,
                                title: orderItem.florist.short_title
                                    ? orderItem.florist.short_title
                                    : orderItem.florist.title,
                            };
                        }
                    }
                } else if (item && item.gd && item.gd.items) {
                    for (const orderItem of item.gd.items) {
                        if (orderItem.grocer && orderItem.grocer.id) {
                            result[orderItem.grocer.id] = {
                                link: '/grocers/' + orderItem.grocer.id,
                                title: orderItem.grocer.short_title
                                    ? orderItem.grocer.short_title
                                    : orderItem.grocer.title,
                            };
                        }
                    }
                } else if (item && item.ld && item.ld.items) {
                    for (const orderItem of item.ld.items) {
                        if (orderItem.laundry && orderItem.laundry.id) {
                            result[orderItem.laundry.id] = {
                                link: '/laundries/' + orderItem.laundry.id,
                                title: orderItem.laundry.short_title
                                    ? orderItem.laundry.short_title
                                    : orderItem.laundry.title,
                            };
                        }
                    }
                } else if (item && item.pd && item.pd.items) {
                    for (const orderItem of item.pd.items) {
                        if (orderItem.pharmacy && orderItem.pharmacy.id) {
                            result[orderItem.pharmacy.id] = {
                                link: '/pharmacies/' + orderItem.pharmacy.id,
                                title: orderItem.pharmacy.short_title
                                    ? orderItem.pharmacy.short_title
                                    : orderItem.pharmacy.title,
                            };
                        }
                    }
                } else if (item && item.ah && item.ah.items) {
                    for (const orderItem of item.ah.items) {
                        if (orderItem.assistant && orderItem.assistant.id) {
                            result[orderItem.assistant.id] = {
                                link: '/assistants/' + orderItem.assistant.id,
                                title: orderItem.assistant.short_title
                                    ? orderItem.assistant.short_title
                                    : orderItem.assistant.title,
                            };
                        }
                    }
                }
                return result;
            },
            getPassTitle(pass) {
                switch (pass) {
                case PASS_TYPE.PASSED: return 'Пропуск оформлен';
                case PASS_TYPE.NOT_PASSED: return 'Пропуск не оформлен';
                case PASS_TYPE.NOT_NEED: return 'Пропуск не требуется';
                default: return '';
                }
            },
            getDeliveryTime(item) {
                if (item.delivery_time) {
                    const deliveryTime = moment(item.delivery_time).format('HH:mm DD.MM');
                    if (item.delivery_now) {
                        return `до ${deliveryTime}`;
                    } else {
                        return `<strong>к ${deliveryTime}</strong>`;
                    }
                }
                return '';
            },
            getTimeRemaining(item) {
                var dt = item.delivery_time;
                if (dt) {
                    dt = new moment(dt);
                    let theDate = new Date();
                    if (
                        item.status === ORDER_STATUS.DONE ||
                        item.status === ORDER_STATUS.CANCELED
                    ) {
                        theDate = moment(item.updated_at).toDate();
                        return (
                            (moment(item.updated_at).diff(dt) > 0 ? '- ' : '') +
                            countdown(
                                theDate,
                                new Date(dt.toDate()),
                                countdown.DAYS | countdown.HOURS | countdown.MINUTES
                            )
                        );
                    }
                    return (
                        (moment().diff(dt) > 0 ? '- ' : '') +
                        countdown(
                            theDate,
                            new Date(dt.toDate()),
                            countdown.DAYS | countdown.HOURS | countdown.MINUTES
                        )
                    );
                }
                return '';
            },
            checkTimeRemaining(item) {
                const doneStatuses = [ORDER_STATUS.DONE, ORDER_STATUS.CANCELED];
                if (doneStatuses.includes(item.status)) {
                    return false;
                }

                const dt = item.delivery_time;
                if (dt) {
                    if (moment().isSameOrAfter(moment(dt))) {
                        return true;
                    }

                    const awaiting = (item.status === ORDER_STATUS.AWAITING) || (item.status === ORDER_STATUS.AWAITING_READY);
                    const delivering = (item.status === ORDER_STATUS.DELIVERY) || (item.status === ORDER_STATUS.DELIVERY_CONCIERGE);
                    const inTwentyMinutes = moment().add(20, 'minutes');
                    const inTenMinutes = moment().add(10, 'minutes');

                    const checkAwaiting = inTwentyMinutes.isSameOrAfter(moment(dt)) && awaiting;
                    const checkDelivering = inTenMinutes.isSameOrAfter(moment(dt)) && delivering;

                    return checkAwaiting || checkDelivering;
                }
                return false;
            },
            cutComment(comment) {
                return `${comment.slice(0, 15)}...`;
            },
            cutDeliveryAddress(address) {
                return `${address.slice(0, 15)}...`;
            },
            getCourierAssignees(item) {
                if (!Array.isArray(item.additional_assignees)) return [];
                return item.additional_assignees.filter(a => a.type === 'courier' && a.user_details);
            },
            getConciergeAssignees(item) {
                if (!Array.isArray(item.additional_assignees)) return [];
                return item.additional_assignees.filter(a => a.type === 'concierge' && a.user_details);
            },
        },
    };
</script>

<style>
.custom-table .position-relative.table-responsive {
    max-height: calc(100vh - 407px);
}
.custom-table.large .position-relative.table-responsive {
    max-height: calc(100vh - 337px);
}

.custom-table table thead th {
    position: sticky;
    box-shadow: 0 0 0 rgba( 0, 0, 0, .3);
    top: -1px;
    background: #fff;
}
.orders .small__input input {
    padding: 0;
}
input.custom-datepicker {
  border: 1px solid;
  border-color: #d8dbe0;
  width: 100%;
}
</style>

<style scoped>
.dot {
    display: inline-block;
    width: 7px;
    height: 7px;
    vertical-align: middle;

    vertical-align: middle;

    background: red;

    border-radius: 50%;
}
.dot.seen {
    background: #23d923;
}
.warning-cell {
    background: red;
    border-radius: 10px;

    color: #fff;
    font-weight: bold !important;
}
.status-cell span {
    display: block;
}
.orders table td,
.orders table th {
    white-space: nowrap;
    padding: 0.1rem;
}
@media (max-width: 576px) {
    .custom-order-1 {
        order: 1;
    }
    .custom-order-2 {
        order: 2;
    }
    .custom-order-3 {
        order: 3;
    }
}
.pass__info {
    display: inline-block;
    width: 20px;
    height: 12px;

    border: 2px solid;
    border-radius: 3px;
}
.not__passed {
    background: red;
    border-color: red;
}
.passed {
    background: #68CE85;
    border-color: #68CE85;
}
.not__need {
    background: transparent;
    border-color: #68CE85;
}
.without__delivery {
    background: transparent;
    border-color: #ff7b49;
}
</style>
